import React from "react";
import { Routes, Route } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ProtectedRoute, PublicRoute } from "./ProtectedRoute";

import TutorPage from "./TutorPage";
import StudentPage from "./StudentPage";
import AdministratorPage from "./AdministratorPage";
import RootPage from "./RootPage";
import LoginPage from "./LoginPage";
import InvoicesList from "./InvoicesList";
import PurchaseInvoicesList from "./PurchaseInvoicesList";
import UAEVATInvoice from "./UAEVATInvoice";
import ProfessionalVATInvoice from "./ProfessionalVATInvoice";
import PurchaseOrderEntry from "./PurchaseOrderEntry";
import Customers from "./Customers";
import Suppliers from "./Suppliers";
import UsersComponent from "./UsersComponent";
import HomePage from "./HomePage";
import SidebarLayout from "./SidebarLayout";


// Placeholder components for new routes
const Dashboard = () => (
  <div className="space-y-6">
    {/* Welcome Section */}
    <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-lg shadow-lg p-6 text-white">
      <h2 className="text-3xl font-bold mb-2">Diamond Quality Constructions</h2>
      <p className="text-emerald-100">Work Management System</p>
      <div className="mt-4 flex items-center space-x-4">
        <div className="flex items-center space-x-2">
          <span className="text-2xl">🇦🇪</span>
          <span>UAE Compliant</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-lg">📊</span>
          <span>Real-time Analytics</span>
        </div>
        <div className="flex items-center space-x-2">
          <span className="text-lg">🛡️</span>
          <span>FTA Approved</span>
        </div>
      </div>
    </div>

    {/* Stats Cards */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Total Invoices</h3>
            <p className="text-3xl font-bold text-emerald-600 mt-2">1,234</p>
            <p className="text-sm text-gray-500 mt-1">+12% from last month</p>
          </div>
          <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">📄</span>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Total Revenue</h3>
            <p className="text-3xl font-bold text-blue-600 mt-2">AED 456,789</p>
            <p className="text-sm text-gray-500 mt-1">+8% from last month</p>
          </div>
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">💰</span>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Pending Tasks</h3>
            <p className="text-3xl font-bold text-orange-600 mt-2">23</p>
            <p className="text-sm text-gray-500 mt-1">5 due today</p>
          </div>
          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">📋</span>
          </div>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">Active Projects</h3>
            <p className="text-3xl font-bold text-purple-600 mt-2">12</p>
            <p className="text-sm text-gray-500 mt-1">3 completed this week</p>
          </div>
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
            <span className="text-2xl">🚀</span>
          </div>
        </div>
      </div>
    </div>

    {/* Recent Activity & Quick Actions */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Recent Activity</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
              <span className="text-sm">📄</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Invoice #INV-2026-0123 created</p>
              <p className="text-xs text-gray-500">2 hours ago</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-sm">💳</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Payment received: AED 25,000</p>
              <p className="text-xs text-gray-500">4 hours ago</p>
            </div>
          </div>
          <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-sm">📋</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">New task assigned: VAT filing</p>
              <p className="text-xs text-gray-500">6 hours ago</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 gap-3">
          <button className="p-4 bg-emerald-50 hover:bg-emerald-100 rounded-lg border border-emerald-200 transition-colors">
            <div className="text-center">
              <span className="text-2xl block mb-2">➕</span>
              <span className="text-sm font-medium text-emerald-800">New Invoice</span>
            </div>
          </button>
          <button className="p-4 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors">
            <div className="text-center">
              <span className="text-2xl block mb-2">👥</span>
              <span className="text-sm font-medium text-blue-800">Add Customer</span>
            </div>
          </button>
          <button className="p-4 bg-orange-50 hover:bg-orange-100 rounded-lg border border-orange-200 transition-colors">
            <div className="text-center">
              <span className="text-2xl block mb-2">📊</span>
              <span className="text-sm font-medium text-orange-800">View Reports</span>
            </div>
          </button>
          <button className="p-4 bg-purple-50 hover:bg-purple-100 rounded-lg border border-purple-200 transition-colors">
            <div className="text-center">
              <span className="text-2xl block mb-2">⚙️</span>
              <span className="text-sm font-medium text-purple-800">Settings</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  </div>
);

const Invoices = () => <InvoicesList />;

const Purchases = () => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Purchases</h2>
      <p className="text-gray-600">Manage purchase orders and suppliers.</p>
    </div>
  </div>
);

const Tasks = () => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Tasks</h2>
      <p className="text-gray-600">Manage your tasks and projects.</p>
    </div>
  </div>
);

const Reports = () => (
  <div className="space-y-6">
    <div className="bg-white rounded-lg shadow-sm p-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Reports</h2>
      <p className="text-gray-600">View financial and business reports.</p>
    </div>
  </div>
);

const RouterMain = () => {
  const { isAuthenticated, logout } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />
      <Route 
        path="/login" 
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } 
      />

      {/* Protected Routes */}
      <Route 
        path="/dashboard" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Dashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoices" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Invoices />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoices/create" 
        element={
          <ProtectedRoute onLogout={logout}>
            <ProfessionalVATInvoice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoices/templates" 
        element={
          <ProtectedRoute onLogout={logout}>
            <div className="p-6"><h2 className="text-2xl font-bold">Invoice Templates</h2></div>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Purchases />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases/orders" 
        element={
          <ProtectedRoute onLogout={logout}>
            <PurchaseOrderEntry />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases/orders/create" 
        element={
          <ProtectedRoute onLogout={logout}>
            <PurchaseOrderEntry />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases/suppliers" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Suppliers />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/purchases/history" 
        element={
          <ProtectedRoute onLogout={logout}>
            <PurchaseInvoicesList />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Tasks />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks/manage" 
        element={
          <ProtectedRoute onLogout={logout}>
            <div className="p-6"><h2 className="text-2xl font-bold">Task Management</h2></div>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/tasks/reports" 
        element={
          <ProtectedRoute onLogout={logout}>
            <div className="p-6"><h2 className="text-2xl font-bold">Task Reports</h2></div>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Reports />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/financial" 
        element={
          <ProtectedRoute onLogout={logout}>
            <div className="p-6"><h2 className="text-2xl font-bold">Financial Reports</h2></div>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/vat" 
        element={
          <ProtectedRoute onLogout={logout}>
            <div className="p-6"><h2 className="text-2xl font-bold">VAT Reports</h2></div>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/sales" 
        element={
          <ProtectedRoute onLogout={logout}>
            <div className="p-6"><h2 className="text-2xl font-bold">Sales Reports</h2></div>
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/reports/custom" 
        element={
          <ProtectedRoute onLogout={logout}>
            <div className="p-6"><h2 className="text-2xl font-bold">Custom Reports</h2></div>
          </ProtectedRoute>
        } 
      />

      {/* Legacy routes */}
      <Route 
        path="/tutor" 
        element={
          <ProtectedRoute onLogout={logout}>
            <ProfessionalVATInvoice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/student" 
        element={
          <ProtectedRoute onLogout={logout}>
            <UsersComponent />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/administrator" 
        element={
          <ProtectedRoute onLogout={logout}>
            <AdministratorPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoice/:id" 
        element={
          <ProtectedRoute onLogout={logout}>
            <UAEVATInvoice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/invoice" 
        element={
          <ProtectedRoute onLogout={logout}>
            <UAEVATInvoice />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/customers" 
        element={
          <ProtectedRoute onLogout={logout}>
            <Customers />
          </ProtectedRoute>
        } 
      />
    </Routes>
  );
};

export default RouterMain;
